import type { ReactNode } from "react";

import type { RecordDetail as Record } from "../record-detail.ts";
import { StateBadge } from "./StateBadge.tsx";

/**
 * The selected row of a board, in full.
 *
 * The right-hand column at 1240 and above, and an overlay over the table below it — the design's
 * own breakpoint, and the reason this is a component rather than a column: at 1024 it is the same
 * panel sliding over the same table, not a second layout. The same arrangement `ExceptionDetail`
 * already uses, and the same `.detail__*` classes, so the six boards that gain a panel here and the
 * one that always had one cannot drift apart visually.
 *
 * `ExceptionDetail` stays its own component and is not folded into this. It carries a timeline, an
 * evidence grid and a set of commands with their own refusals — a panel that *acts*. This one is a
 * panel that *reads*, which is what the other six boards' designs draw, and merging them would mean
 * one component with half its props unused on either side of the fork.
 *
 * ## Why it closes on Escape and returns focus
 *
 * Below 1240 it covers the table, so a keyboard user who opened a row has their focus inside
 * something they cannot see around. Escape closes it and focus goes back to the row that opened it,
 * which the board owns — this component reports the intent and does not reach for the DOM itself.
 */

export interface RecordDetailProps {
  readonly record: Record;
  /** Closes the panel. Below the design's 1240 breakpoint this is the only way out of the overlay. */
  readonly onClose: () => void;
  /** True while the panel is the overlay rather than the column, which is what `--open` styles. */
  readonly open: boolean;
  /**
   * What this row can be commanded to do, rendered under the fields.
   *
   * A slot rather than a shape, the way `PageHeading` takes its `action`: every board that
   * commands has a different confirmation to draw — the exceptions panel has four actions with
   * their own notes, and the trips board has one that asks for a reason — and a shape general
   * enough for both would describe neither. Optional, because most boards on this console read
   * and do not act.
   */
  readonly action?: ReactNode;
}

export function RecordDetail({ record, onClose, open, action }: RecordDetailProps): ReactNode {
  return (
    <aside
      className={open ? "detail detail--open" : "detail"}
      aria-label={`${record.headline} — details`}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="detail__head">
        {/*
          The design puts a back control here only in the overlay, where the panel covers the table.
          It is rendered at every width and hidden by the stylesheet below its own breakpoint,
          because a control that exists only at one width is a control a keyboard user loses when
          the window is resized under them.
        */}
        <button type="button" className="detail__back" onClick={onClose} aria-label="Close details">
          <span aria-hidden="true">arrow_back</span>
        </button>
        <span className="detail__ident">
          <span className="detail__headline">{record.headline}</span>
          <span className="detail__source">{record.source}</span>
        </span>
        {record.state === undefined ? null : (
          <StateBadge
            code={record.state.code}
            status={record.state.status}
            classKey={record.state.classKey}
            label={record.state.label}
          />
        )}
      </div>

      <div className="detail__body">
        <div className="detail__fields">
          {/*
            The identifier is first and always present. Every other line is what the board's own
            operation answered, so a panel never asks for anything the row did not already carry.
          */}
          <div className="detail__field">
            <span className="detail__field-name">Identifier</span>
            <span className="detail__field-value detail__field-value--mono">
              {record.identifier}
            </span>
          </div>
          {record.fields.map((entry) => (
            <div className="detail__field" key={entry.name}>
              <span className="detail__field-name">{entry.name}</span>
              <span
                className={
                  entry.absent === true
                    ? "detail__field-value detail__field-value--absent"
                    : entry.mono === true
                      ? "detail__field-value detail__field-value--mono"
                      : "detail__field-value"
                }
                {...(entry.title === undefined ? {} : { title: entry.title })}
              >
                {entry.value}
              </span>
            </div>
          ))}
        </div>
        {record.note === undefined ? null : <p className="detail__footnote">{record.note}</p>}
        {action === undefined ? null : <div className="detail__actions">{action}</div>}
      </div>
    </aside>
  );
}
